import React, { useEffect, useState } from 'react';
import { Button, Skeleton, Stack, Typography } from '@mui/material';
import moment from 'moment';
import { getUserEventsInstancesForUser } from 'data/api/event.api';
import { EventInstance } from 'data/models/loss-event.model';

// Every piece of this section's state carries the member it was fetched for.
// Clearing on a member change inside an effect is too late — the rail
// re-renders under the newly selected family before effects run, so the
// previous family's patient name and date of death would be shown once.
// Ownership is checked at read time instead, so a response for a member who
// is no longer selected has no way to reach the screen. Same shape as
// useDraftReply, for the same reason.
interface EventState {
  owner?: string;
  event: EventInstance | null;
  loading: boolean;
  error: string;
  // A 403 is not a transient failure, so it must not be offered a retry that
  // will fail the same way every time.
  retryable: boolean;
}

const EMPTY: EventState = {
  owner: undefined,
  event: null,
  loading: false,
  error: '',
  retryable: false,
};

const recordedAt = (event: EventInstance): number => {
  const time = event.createdDate ? moment(event.createdDate).valueOf() : NaN;
  return Number.isFinite(time) ? time : 0;
};

// The endpoint answers with every event the member is *related* to, owned and
// collaborator alike — its query walks user_events_collaborators, and each row
// carries the owning member in userId. Only an event this member owns may be
// rendered as their current event: without that filter, a member invited onto
// another family's event is shown that family's patient, disease, date of death
// and internal notes, labelled as their own. Three members on production sit in
// exactly that shape today, and one of them is in a different company from the
// event's owner.
const ownedBy = (userId: string) => (event: EventInstance) =>
  event.userId === userId;

// "Current event" is the most recently recorded one, never whatever the API
// happened to return first. deathDate cannot order the list — it is unset on
// 41% of production rows and has a separate free-text range form — while
// createdDate is always set. userEventId breaks exact ties so the order is
// total and the same card renders every time.
const mostRecent = (
  userId: string,
  events?: EventInstance[],
): EventInstance | null => {
  const owned = events?.filter(ownedBy(userId));
  if (!owned?.length) return null;
  return owned.sort((a, b) => {
    const byRecorded = recordedAt(b) - recordedAt(a);
    if (byRecorded !== 0) return byRecorded;
    return (b.userEventId || '').localeCompare(a.userEventId || '');
  })[0];
};

// The api interceptor rejects with the backend's message string, so the HTTP
// status is gone by the time it lands here. Nest sends "Forbidden resource"
// on a 403 — the only signal we have that retrying is pointless.
export const isForbidden = (error: unknown): boolean => {
  const text = typeof error === 'string' ? error : (error as Error)?.message;
  return !!text && text.toUpperCase().startsWith('FORBIDDEN');
};

const patientLabel = (event: EventInstance): string => {
  if (!event.relatedRoleName) return '';
  return event.relatedRoleRelationship
    ? `${event.relatedRoleName} (${event.relatedRoleRelationship})`
    : event.relatedRoleName;
};

const deathDateLabel = (event: EventInstance): string =>
  event.deathDate
    ? moment.utc(event.deathDate).local().format('MM/DD/YYYY')
    : '';

export const CARD = {
  border: '1px solid #EFEDEB',
  borderRadius: '10px',
  backgroundColor: '#FFFFFF',
};

export const LINK_BUTTON_SX = {
  color: '#356766',
  textTransform: 'none',
  fontWeight: 600,
  paddingX: 0,
  minWidth: 0,
};

export const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{ ...CARD, fontSize: '13px', color: '#7D818C', padding: '10px 12px' }}
  >
    {children}
  </Typography>
);

interface IEventDetailsProps {
  userId: string;
}

export const EventDetails: React.FC<IEventDetailsProps> = ({ userId }) => {
  const [state, setState] = useState<EventState>(EMPTY);
  const [attempt, setAttempt] = useState<number>(0);

  useEffect(() => {
    let live = true;
    setState({ ...EMPTY, owner: userId, loading: true });
    getUserEventsInstancesForUser(userId)
      .then((response) => {
        if (!live) return;
        setState({
          ...EMPTY,
          owner: userId,
          event: mostRecent(userId, response?.userEvents),
        });
      })
      .catch((error) => {
        if (!live) return;
        const forbidden = isForbidden(error);
        setState({
          ...EMPTY,
          owner: userId,
          error: forbidden
            ? 'You do not have permission to see this family’s event details.'
            : 'Could not load event details.',
          retryable: !forbidden,
        });
      });
    return () => {
      live = false;
    };
  }, [userId, attempt]);

  // Anything belonging to another member reads as not-yet-loaded, so there is
  // no window in which the previous family's details are on screen and no
  // frame in which a member who has events looks like a member who has none.
  const owned = state.owner === userId ? state : null;
  const loading = !owned || owned.loading;
  const event = owned?.event || null;

  if (owned?.error) {
    return (
      <Stack
        sx={{
          ...CARD,
          padding: '10px 12px',
          gap: '2px',
          alignItems: 'flex-start',
        }}
      >
        <Typography sx={{ fontSize: '13px', color: '#7D818C' }}>
          {owned.error}
        </Typography>
        {owned.retryable && (
          <Button
            size="small"
            onClick={() => setAttempt((current) => current + 1)}
            sx={{
              color: '#356766',
              textTransform: 'none',
              fontWeight: 600,
              paddingX: 0,
              minWidth: 0,
            }}
          >
            Try again
          </Button>
        )}
      </Stack>
    );
  }

  if (!loading && !event) {
    return <Note>No event has been recorded for this family yet.</Note>;
  }

  const rows = [
    {
      label: 'Current event',
      // US-5 maps this to events.abbreviated_name, and so does the user's
      // loss-events screen. eventName is the sentence fragment the member app
      // asks with ("managing a death"), not a label — the two differ on 30,249
      // of 30,255 production events.
      value: event?.eventAbbreviatedName || '',
      strong: true,
      placeholder: '55%',
    },
    {
      label: 'Patient',
      value: event ? patientLabel(event) : '',
      strong: true,
      placeholder: '68%',
    },
    {
      label: 'Disease',
      value: event?.diseaseModel?.diseaseName || '',
      strong: false,
      placeholder: '48%',
    },
    {
      label: 'Date of death',
      value: event ? deathDateLabel(event) : '',
      strong: false,
      placeholder: '40%',
    },
    {
      label: 'Internal notes',
      value: event?.internalNotes || '',
      strong: false,
      placeholder: '85%',
    },
  ];

  return (
    <Stack component="dl" sx={{ ...CARD, margin: 0 }}>
      {rows.map((row, index) => (
        <Stack
          key={row.label}
          direction="row"
          gap={1.5}
          sx={{
            padding: '8px 12px',
            borderTop: index === 0 ? 'none' : '1px solid #F4F2F0',
          }}
        >
          <Typography
            component="dt"
            sx={{
              width: '104px',
              flex: 'none',
              fontSize: '13px',
              fontWeight: 600,
              lineHeight: '20px',
              color: '#7D818C',
            }}
          >
            {row.label}
          </Typography>
          <Typography
            component="dd"
            sx={{
              margin: 0,
              minWidth: 0,
              flex: 1,
              fontSize: '14px',
              fontWeight: row.strong ? 600 : 400,
              lineHeight: '20px',
              color: row.value ? '#213B54' : '#9A9A9A',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
            }}
          >
            {loading ? <Skeleton width={row.placeholder} /> : row.value || '—'}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};
